<script setup lang="ts">
import type { SubmitEventPromise } from 'vuetify';
import type { ReadonlyDeep } from 'type-fest';
import type {
  InitialData as JobEditSplitToBaseDialogFormInitialData,
  ValidData as JobEditSplitToBaseDialogFormValidData,
} from '@/components/dialogForms/JobEditSplitToBaseDialogForm.vue';

import { computed, useTemplateRef } from 'vue';

import KBtn from '@/components/btns/KBtn.vue';
import JobEditSplitToBaseDialogForm from '@/components/dialogForms/JobEditSplitToBaseDialogForm.vue';
import ReportGenerateDialogHeader from '@/components/headers/ReportGenerateDialogHeader.vue';
import PageDialogWrapper from '@/components/wrappers/PageDialogWrapper.vue';

export interface Props {
  isConfirmLoading?: boolean;
  stopsCount?: number;
  job?: ReadonlyDeep<{
    rockhopper_id: number;
    rockhopper_date: string;
    driver_code: number;
  }>;
  stopNumber?: 2 | 3;
  warehouse?: string;
  confirm?: (
    formData: JobEditSplitToBaseDialogFormValidData,
  ) => Promise<unknown>;
}
export type DefProps = typeof props;

const props = withDefaults(defineProps<Props>(), {
  isConfirmLoading: false,
  stopsCount: 2,
  job: undefined,
  stopNumber: undefined,
  warehouse: undefined,
  confirm: undefined,
});

const emit = defineEmits<{
  cancelBtnClick: [event: Event];
  confirmBtnClick: [
    event: SubmitEventPromise | null | undefined,
    formData: JobEditSplitToBaseDialogFormValidData,
    props: DefProps,
  ];
}>();

function handleCancelClick(event: Event): void {
  if (props.isConfirmLoading) return;
  emit('cancelBtnClick', event);
}

function handleSubmit(
  event: SubmitEventPromise | null | undefined,
  formData: JobEditSplitToBaseDialogFormValidData,
): void {
  if (props.isConfirmLoading) return;
  emit('confirmBtnClick', event, formData, props);
}

const mainEl = useTemplateRef<HTMLElement>('main');

function getSubmitBtn(): HTMLInputElement | null {
  if (!mainEl.value) return null;
  return mainEl.value.querySelector('input[type=submit]');
}

function handleConfirmClick(event: Event): void {
  const submitBtn = getSubmitBtn();
  if (!submitBtn) return;
  submitBtn.click();
}

const initialData = computed<JobEditSplitToBaseDialogFormInitialData>(() => {
  return {
    stopNumber: props.stopNumber || 2,
    warehouse: props.warehouse,
  };
});
</script>

<template>
  <PageDialogWrapper
    :class="$style.jobEditSplitToBaseDialog"
    data-testid="jobEditSplitToBaseDialog"
  >
    <template #header>
      <ReportGenerateDialogHeader @close-click="handleCancelClick">
        Split to base
      </ReportGenerateDialogHeader>
    </template>

    <section ref="main">
      <div :class="$style.text">Choose stop and warehouse.</div>
      <JobEditSplitToBaseDialogForm
        :initial-data="initialData"
        :stops-count="props.stopsCount"
        :loading="props.isConfirmLoading"
        @submit="handleSubmit"
      />
    </section>

    <template #footer>
      <menu :class="$style.dialogFooterMenuList">
        <li :class="$style.dialogFooterMenuListItem">
          <KBtn
            variant="outlined"
            append-icon="mdi-close"
            :disabled="props.isConfirmLoading"
            @click="handleCancelClick"
          >
            Cancel
          </KBtn>
        </li>
        <li :class="$style.dialogFooterMenuListItem">
          <KBtn
            append-icon="mdi-check"
            :loading="props.isConfirmLoading"
            @click="handleConfirmClick"
          >
            Confirm
          </KBtn>
        </li>
      </menu>
    </template>
  </PageDialogWrapper>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use 'sass:math';
@use '@/styles/colors';
@use '@/styles/variables';

.jobEditSplitToBaseDialog {
  width: variables.$reportGenerateDialogWidth;
}
.text {
  margin: 0 0 math.div(variables.$pageDialogWindowMainWrapperMargin, 2) 0;
}
.dialogFooterMenuList {
  list-style: none;
  margin: 0;
  padding: 0;
  text-align: right;
}
.dialogFooterMenuListItem {
  display: inline;
}
.dialogFooterMenuListItem + .dialogFooterMenuListItem {
  margin-left: 1rem;
}
</style>
